import React, {
  CSSProperties,
  FC,
  ReactNode,
  createContext,
  useContext,
} from 'react'
import { Col, ColProps, Row, RowProps } from 'antd'
import useDimensions from 'react-cool-dimensions'

import { Typography, TTypographyProps } from 'src/storybook/components'

import { TFormVariant, useFormFnUIContext } from './FormUI'
import { theme } from './theme'

const variants = {
  basic: {
    padding: 0,
  },
  padded: {
    padding: 24,
  },
} as const

export type TFormSectionBreakPoint = 'XS' | 'SM' | 'MD' | 'LG'

type TFormSectionContext = {
  breakpoint: TFormSectionBreakPoint
}

const Context = createContext<TFormSectionContext>({
  breakpoint: 'SM',
})

export const useFormSectionContext = () => useContext(Context)

export type TFormSectionProps =
  | {
      title?: ReactNode
      /**
       * Needed for integration with LayoutFormAnchor:18
       * The only usecase is when you use an anchor layout and
       * want your actual title to be different from the anchor
       * title showed in the sidebar navigator.
       */
      anchorTitle?: ReactNode
      description?: ReactNode
      disableColWrapped?: boolean
      disableChildRowWrapped?: boolean
      colProps?: ColProps
      rowProps?: RowProps
      typography?: TTypographyProps
      variant?: TFormVariant
      style?: CSSProperties
      className?: string
      id?: string
      footer?: ReactNode
      header?: ReactNode
    } & (
      | {
          disableColWrapped: true
          colProps?: never
        }
      | {
          disableColWrapped?: false
          colProps?: ColProps
        }
    ) &
      (
        | {
            disableChildRowWrapped?: false
            rowProps?: RowProps
          }
        | {
            disableChildRowWrapped: true
            rowProps?: never
          }
      )

export const FormSection: FC<TFormSectionProps> = ({
  title,
  description,
  children,
  disableColWrapped,
  disableChildRowWrapped,
  colProps,
  rowProps,
  typography,
  variant: variantFromProps,
  style,
  className,
  id,
  footer,
  header,
}) => {
  const { variant: variantFromContext } = useFormFnUIContext()

  const variant = variantFromProps || variantFromContext
  const variantProps = variants[variant]

  const { observe, currentBreakpoint, width } = useDimensions({
    breakpoints: { XS: 0, SM: 300, MD: 550, LG: 1200 } as Record<
      TFormSectionBreakPoint,
      number
    >,
  })
  const isBig = width >= 1030

  const ui = (
    <Row
      ref={observe}
      wrap={!isBig}
      style={{
        background: 'white',
        width: '100%',
        padding: variantProps?.padding,
        gap: '16px 32px',
      }}
    >
      {title && (
        <div
          style={{
            width: isBig ? 150 : '100%',
            flexShrink: 0,
          }}
        >
          <Typography
            id={id}
            variant="subHeader-16"
            weight="semi-bold"
            ellipsis={false}
            {...typography}
          >
            {title}
          </Typography>
        </div>
      )}
      <Row
        style={{
          width: '100%',
          maxWidth: isBig ? theme.maxWidth : undefined,
          ...style,
        }}
        className={className}
        gutter={[0, 16]}
      >
        {header && <Col xs={24}>{header}</Col>}
        {description && (
          <Col xs={24}>
            <Typography type="secondary" ellipsis={false}>
              {description}
            </Typography>
          </Col>
        )}
        {children && (
          <Col xs={24}>
            {disableChildRowWrapped ? (
              <>{children}</>
            ) : (
              <Row gutter={[theme.itemHorizontalSpacing, 16]} {...rowProps}>
                {children}
              </Row>
            )}
          </Col>
        )}
        {footer && <Col xs={24}>{footer}</Col>}
      </Row>
    </Row>
  )

  return (
    <Context.Provider
      value={{
        breakpoint: currentBreakpoint as TFormSectionBreakPoint,
      }}
    >
      {disableColWrapped ? (
        <>{ui}</>
      ) : (
        <Col xs={colProps?.xs ?? 24} {...colProps}>
          {ui}
        </Col>
      )}
    </Context.Provider>
  )
}

export default FormSection
